import { validate as emailValidator } from "email-validator";
import { parseDate, yearsAgo } from "../../../utils/helpers";
import { State, profileType_Fiscal } from "../../../state-actions-reducers";
import { allFiscalCountriesKz } from "../../../content/countries";
import { Option } from "../../../types/common-types";
import { validatePhone, validateTg } from "../../validators";
import { FoundingDocument } from "../../../enums/foundingDocumentEnum";

interface VlidateDateArgs {
  current: Date | string;
  min?: Date;
  max?: Date;
  required?: boolean;
}

export function smartValidateString(string: string): boolean {
  const trimmedString = (string??'').trim();
  const letter = trimmedString.match(/[a-zA-Zа-яёА-ЯЁ]/g)?.length ?? 0;
  const nums = trimmedString.match(/[0-9]/g)?.length ?? 0;
  const symbols = trimmedString.match(/[\<\>\@\!\#\$\%\^\&\*\(\)\_\+\[\]\{\}\?\:\;\|\'\"\,\.\/\~\`\\\-\=\']/g)?.length ?? 0;
  return /[a-zA-Zа-яёА-ЯЁ]/g.test(trimmedString) && symbols <= letter * 2 && nums <= letter * 4;
}

export function validateDate({ current, min, max, required }: VlidateDateArgs): boolean {
  // By default allow empty strings. Make stronger checking on submitting
  const currentValue = parseDate(current);
  if (!currentValue) {
    return !required;
  }
  if (min && max) {
    return min <= currentValue && currentValue <= max;
  }
  if (min) {
    return min <= currentValue;
  }
  if (max) {
    return currentValue <= max;
  }
  return true;
}

export function validateDatesRange(from: Date | string, to: Date | string) {
  const smallerDate = parseDate(from);
  const biggerDate = parseDate(to);
  if (!smallerDate || !biggerDate) {
    return false;
  }
  return smallerDate < biggerDate;
}

export function validateCollegeDates(startDate: string | Date, endDate: string | Date, required = false) {
  if (!startDate || !endDate) return !required;
  // Check if dates is valid
  return validateDatesRange(startDate, endDate);
}

// By default allow empty strings
// Will make stronger validation on submitting
export function validateString(string: string, maxLength: number, required?: boolean, regexp?: RegExp): boolean {
  const minLength = required ? 1 : 0;
  const trimmedString = (string??'').trim();
  return trimmedString.length >= minLength && trimmedString.length <= maxLength && Boolean(trimmedString.match(regexp || ""));
}

export function validateAddress(name: string, required?: boolean): boolean {
  return validateString(name, 200, required);
}

export function validateBankAccount(name: string, required?: boolean): boolean {
  return validateString(name, 200, required);
}

export function validateName(name: string, required?: boolean): boolean {
  return validateString(name, 200, required);
}

export function birthPlace(place: string, required?: boolean): boolean {
  return validateString(place, 300, required);
}

export function stringDigitsLength(digits: string, digitslength: number, required = true): boolean {
  return validateString(digits, 100, false) && ((required && digits.length == digitslength) || !required);
}

export function passportDigits(digits: string, required?: boolean): boolean {
  return validateString(digits, 100, required);
}

export function passportText(text: string, required?: boolean): boolean {
  return validateString(text, 400, required);
}

export function validateEmail(text: string, required?: boolean): boolean {
  if (!text && !required) return true;
  return validateString(text, 100, required) && emailValidator(text);
}

export function validateReferences(values: string[], required = false): boolean {
  if (!required && values.length === 0) return true;
  if (required && values.length === 0) return false;
  if (values.length < 20) {
    return values.every((value) => {
      return validateString(value, 200);
    });
  }
  return false;
}

const URL_REGEXP =
  /^(https?:\/\/)?(www\.)?[-a-zа-яё0-9@:%._\+~#=]{2,256}\.[a-zа-яё]{1,4}(?::[0-9]+)?(\/?[-a-zа-яё0-9@:%_\+~#]+(\.[a-zа-яё0-9]+)?)+(\?[-а-яёa-z0-9@:%._\+~#]+(=[-a-zа-яё0-9@:%._\+~#]+)?((\&[-a-zа-яё0-9@:%._\+~#]+(=[-a-zа-яё0-9@:%._\+~#]+)?)+)?)?/gi;
export function validateUrl(url: string, maxLength = 300, required = false): boolean {
  if (!url && !required) return true;
  if (url) {
    if (typeof url === "string") {
      if (url.length < maxLength) {
        return Boolean(url.match(URL_REGEXP));
      }
    }
  }
  return false;
}

export function validateSocialLink(value: string, required = false): boolean {
  return validateUrl(value, 300, required);
  return false;
}

export function validatePassportNumber(value: string, required = false, minLength?: number, maxLength?: number) {
  return validateString(value, maxLength || 10, required) && value.replace(/[^a-zA-Zа-яА-Я0-9 ]/g, "").length >= (minLength || 7);
}

export function validateStepCompanyKzt(step: number, state: State): string[] {
  let selectedCitizenship, countryRegistration;
  allFiscalCountriesKz.map((country: Option) => {
    if (country.value === state.citizenship) selectedCitizenship = country.label.rus;
    if (country.value === state.countryRegistration) countryRegistration = country.label.rus;
  });
  switch (step) {
    case 1:
      return [
        // // Array of invalid field names
        // // validate(): boolean && 'field name' -> null or 'field name'
        // // Then this array filtered with the Boolean()
        // !validateString(state.mainCity, 100, true) && 'mainCity',
        !validateName(state.F_FiscalType, true) && "F_FiscalType",
        !smartValidateString(state.headFirstname) && "headFirstname",
        !smartValidateString(state.headLastname) && "headLastname",
        !smartValidateString(state.headPatronymic) && !state.noPatronymic && "headPatronymic",
        // !validateString(state.certificateRegistrationNumber, 30, true) && 'certificateRegistrationNumber',
        !validateDate({
          current: state.registeredDate,
          required: true,
        }) && "registeredDate",
        !stringDigitsLength(state.F_INN, 12) && "F_INN",
        state.stateRegistrationCertificate.length === 0 && "stateRegistrationCertificate",
        Number(state.foundingDocument) === FoundingDocument.NotSelected && "foundingDocument",
        !validatePassportNumber(state.headProxyNumber, true, 1, 10) && Number(state.foundingDocument) === FoundingDocument.PowerOfAttorney && "headProxyNumber",
        !validateDate({
          current: state.headProxyStart,
          required: true,
        }) &&
          Number(state.foundingDocument) === FoundingDocument.PowerOfAttorney &&
          "headProxyStart",
        !validateDate({
          current: state.headProxyEnd,
          required: true,
        }) &&
          Number(state.foundingDocument) === FoundingDocument.PowerOfAttorney &&
          "headProxyEnd",
        !validateString(state.addressFactualCountry, 300, true) && "addressFactualCountry",
        !validateString(state.addressFactualRegion, 300, true) && "addressFactualRegion",
        !validateString(state.addressFactualCity, 300, true) && "addressFactualCity",
        !smartValidateString(state.addressFactualStreet) && "addressFactualStreet",
        !validateString(state.addressFactualBuilding, 300, true) && "addressFactualBuilding",
        // !validateString(state.addressFactualApartments, 300, true) && !state.addressFactualNoApartments && "addressFactualApartments",
        // !validateString(state.addressFactualPremises, 300, true) && "addressFactualPremises",
        !validateString(state.addressFactualOffice, 300, true) && !state.addressFactualNoApartments && "addressFactualOffice",
        state.addressRegisteredPostCode.length < 6 && "addressRegisteredPostCode",
        !validateString(state.addressRegisteredCountry, 300, true) && "addressRegisteredCountry",
        !validateString(state.addressRegisteredRegion, 300, true) && "addressRegisteredRegion",
        !validateString(state.addressRegisteredCity, 300, true) && "addressRegisteredCity",
        !smartValidateString(state.addressRegisteredStreet) && "addressRegisteredStreet",
        !validateString(state.addressRegisteredBuilding, 300, true) && "addressRegisteredBuilding",
        // !validateString(state.addressRegisteredApartments, 300, true) &&
        //   !state.addressRegisteredNoApartments &&
        //   'addressRegisteredApartments',
        // !validateString(state.addressRegisteredPremises, 300, true) && "addressRegisteredPremises",
        !validateString(state.addressRegisteredOffice, 300, true) && !state.addressRegisteredNoApartments && "addressRegisteredOffice",
        state.addressFactualPostCode.length < 6 && "addressFactualPostCode",
        !smartValidateString(state.headPosition) && "headPosition",
        !validateName(state.headPositionGenitive, true) && "headPositionGenitive",
        !validateString(state.foundingDocument, 100, true) && "foundingDocument",
        state.foundingDocument_Image.length === 0 && "foundingDocument_Image",
        !smartValidateString(state.headFirstnameGenitive) && "headFirstnameGenitive",
        !smartValidateString(state.headLastnameGenitive) && "headLastnameGenitive",
        !smartValidateString(state.headPatronymicGenitive) && !state.noPatronymic && !state.noPatronymic && "headPatronymicGenitive",
        state.F_Scan_Directors_Passport.length === 0 && "F_Scan_Directors_Passport",
        state.F_Scan_Directors_Passport_Address.length === 0 && "F_Scan_Directors_Passport_Address",
        !validateString(state.F_FullName, 1000, true) && "F_FullName",
        !validateString(state.F_ShortName, 1000, true) && "F_ShortName",
        state.oKED.replace(/_/gim, "").length > 0 && state.oKED.replace(/_/gim, "").length < 5 && "oKED",
        state.oKPO.replace(/_/gim, "").length > 0 && state.oKPO.replace(/_/gim, "").length < 8 && "oKPO",
        state.F_KBe.replace(/_/gim, "").length > 0 && state.F_KBe.replace(/_/gim, "").length < 2 && "F_KBe",
        state.vatCertificate.replace(/_/gim, "").length > 0 && state.vatCertificate.replace(/_/gim, "").length < 12 && "vatCertificate",
      ].filter(Boolean) as string[];
    case 2:
      return [
        ...state.BankDetails.map((bank, i) => {
          if (bank.F_BankRS.length < 12) return `F_BankRS${i}`;
        }),
        ...state.BankDetails.map((bank, i) => {
          if (bank.F_BankBIK.length < 8) return `F_BankBIK${i}`;
        }),
        ...state.BankDetails.map((bank, i) => {
          if (!smartValidateString(bank.F_BankName)) return `F_BankName${i}`;
        }),
      ].filter(Boolean) as string[];
    case 3:
      let messengersFields: string[] = [];
      messengersFields = state.messengers
        .map((e, index) => {
          return e.type === "Telegram" || e.type === "Max" || (e.type === "WhatsApp" && e.isCheck) ? !validateTg(e.link) && `messengersLink${index}` : !validatePhone(e.link) && `messengersLink${index}`;
        })
        .filter(Boolean) as string[];
      return [
        state?.contactPhoneSeconds?.[0]?.length > 0 && !state?.contactPhoneSeconds?.some((number) => validatePhone(number)) && "contactPhoneSeconds",
        state?.telegram[0]?.replace(/[^A-Za-z0-9@_]/g, "").length > 0 &&
          (state?.telegram[0]?.replace(/[^A-Za-z0-9_]/g, "").length < 5 || (state?.telegram[0]?.match(/[a-zA-Zа-яёА-ЯЁ]/g) || "")?.length < 1) &&
          "telegram",
        state?.personalWebsite[0]?.length > 0 && !validateUrl(state?.personalWebsite[0], 300, true) && "personalWebsite",
        !state.contactPhones.some((number) => validatePhone(number)) && "contactPhones",
        !state.contactEmails.some((contactEmails) => validateEmail(contactEmails, true)) && "contactEmails",
        state?.personalWebsite[0]?.length > 0 && !validateUrl(state?.personalWebsite[0], 300, true) && "personalWebsite",
        !smartValidateString(state.purposeOfLease) && "purposeOfLease",
        !state.messengers.some((messenger) => validateString(messenger.type, 200, true)) && "messengersType",
        ...messengersFields,
        !state.additionalLinksDescription.some((desc) => validateString(desc, 300, true)) && "additionalLinksDescription",
        !state.additionalLinks.some((links) => validateString(links, 200, true)) && "additionalLinks",
        !smartValidateString(state.contactPersonFirstname) && "contactPersonFirstname",
        !smartValidateString(state.contactPersonLastname) && "contactPersonLastname",
        !smartValidateString(state.contactPersonPatronymic) && !state.noPatronymicContact && "contactPersonPatronymic",
        !state.contactPersonPhones.some((number) => validatePhone(number)) && "contactPersonPhones",
        !state.contactPersonEmails.some((contactPersonEmails) => validateEmail(contactPersonEmails, true)) && "contactPersonEmails",
        !smartValidateString(state.contactPersonPosition) && "contactPersonPosition",
      ].filter(Boolean) as string[];
    case 4:

    default:
      return [];
  }
}

export function validateForm(state: State): string[] {
  return [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].reduce((result: string[], step: number): string[] => [...result, ...validateStepCompanyKzt(step, state)], []);
}

export function trimForm(state: State): State {
  return Object.keys(state).reduce((trimmedState: State, key) => {
    switch (key) {
      case "mobilePhoneNumbers":
      case "F_WorkPhoneNumber":
      case "contactPhones":
      case "addressRegisteredPhones":
      case "addressFactualPhones":
      case "workPhoneNumbers":
        // Filter out invalid values
        return {
          ...trimmedState,
          [key]: state[key].filter(validatePhone),
        };
      case "emails":
        return {
          ...trimmedState,
          emails: state.emails.filter((email) => validateEmail(email, true)),
        };
      case "F_Email":
        return {
          ...trimmedState,
          emails: state.emails.filter((email) => validateEmail(email, true)),
        };
      case "vk":
      case "facebook":
      case "youtube":
      case "vimeo":
      case "instagram":
      case "workWebsite":
      case "personalWebsite":
      case "additionalLinks":
      case "addressRegisteredDate":
        return {
          ...trimmedState,
          addressRegisteredDate: validateDate({
            current: state.addressRegisteredDate,
            max: new Date(),
          })
            ? state.addressRegisteredDate
            : "",
        };
      case "isStudent":
        return {
          ...trimmedState,
          collegeStartedAt: state.isStudent ? state.collegeStartedAt : "",
          collegeEndedAt: state.isStudent ? state.collegeEndedAt : "",
        };
      default:
        return trimmedState;
    }
  }, state);
}
